import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { TOKEN_KEY, UNAUTHORIZED_EVENT, USER_KEY, api } from '../services/api.js';

const AuthContext = createContext(null);

const readStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem(USER_KEY));
  } catch {
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState(readStoredUser);
  const [loading, setLoading] = useState(Boolean(token));
  const [sessionExpired, setSessionExpired] = useState(false);

  const clearSession = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setToken(null);
    setUser(null);
  }, []);

  useEffect(() => {
    let mounted = true;
    if (!token) {
      setLoading(false);
      return undefined;
    }

    api
      .get('/auth/me')
      .then(({ data }) => {
        if (!mounted) return;
        setUser(data.user);
        setSessionExpired(false);
        localStorage.setItem(USER_KEY, JSON.stringify(data.user));
      })
      .catch((error) => {
        if (!mounted) return;
        // Only an auth failure invalidates the session. A 500 or a dropped
        // connection is the server's problem, not a reason to sign the user out
        // and lose whatever they were doing.
        if ([401, 403].includes(error?.response?.status)) clearSession();
      })
      .finally(() => mounted && setLoading(false));

    return () => {
      mounted = false;
    };
  }, [token, clearSession]);

  // The axios interceptor fires this when any request comes back 401, so an
  // expired session drops straight to the login screen instead of leaving a
  // rendered shell whose every query fails.
  useEffect(() => {
    const onUnauthorized = () => {
      setSessionExpired(true);
      clearSession();
    };

    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
  }, [clearSession]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    setSessionExpired(false);
    setToken(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(() => {
    setSessionExpired(false);
    clearSession();
  }, [clearSession]);

  const value = useMemo(
    () => ({
      token,
      user,
      loading,
      sessionExpired,
      isAuthenticated: Boolean(token && user),
      login,
      logout,
      acknowledgeExpiry: () => setSessionExpired(false),
    }),
    [token, user, loading, sessionExpired, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
